import React, { useState, useEffect } from 'react';
import { Snackbar, SnackbarContent, Button, Box, Typography } from '@mui/material';
import { Warning as WarningIcon, Refresh as RefreshIcon } from '@mui/icons-material';

const RateLimitSnackbar = ({ 
    open, 
    onClose, 
    message = "Too many requests. Please wait before trying again.",
    retryAfter = null,
    onRetry = null 
}) => {
    const [countdown, setCountdown] = useState(0);
    const [canRetry, setCanRetry] = useState(false);

    useEffect(() => {
        if (open && retryAfter) {
            const initialCountdown = parseInt(retryAfter);
            setCountdown(initialCountdown);
            setCanRetry(false);

            const timer = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        setCanRetry(true);
                        clearInterval(timer);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(timer);
        } else if (open && !retryAfter) {
            // If no retry-after header, allow retry after 5 seconds
            const timer = setTimeout(() => {
                setCanRetry(true);
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [open, retryAfter]);

    const handleRetry = () => {
        if (onRetry) {
            onRetry();
        }
        onClose();
    };

    const getCountdownText = () => {
        if (countdown > 0) {
            return `Please wait ${countdown} seconds before retrying.`;
        }
        return message;
    };

    return (
        <Snackbar
            open={open}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            autoHideDuration={retryAfter ? null : 8000} // Don't auto-hide if we have a countdown
            onClose={onClose}
            sx={{
                maxWidth: 600,
            }}
        >
            <SnackbarContent
                sx={{
                    backgroundColor: '#ff9800', // Orange warning color
                    color: '#000',
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 300,
                }}
                message={
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <WarningIcon sx={{ mr: 1, color: '#000' }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Rate Limit Reached
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', opacity: 0.9 }}>
                                {getCountdownText()}
                            </Typography>
                        </Box>
                    </Box>
                }
                action={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {onRetry && (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={handleRetry}
                                disabled={!canRetry}
                                sx={{
                                    color: '#000',
                                    borderColor: '#000',
                                    '&:hover': {
                                        borderColor: '#333',
                                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                    },
                                    '&.Mui-disabled': {
                                        color: 'rgba(0, 0, 0, 0.4)',
                                        borderColor: 'rgba(0, 0, 0, 0.4)',
                                    }
                                }}
                            >
                                {countdown > 0 ? `Retry (${countdown}s)` : 'Retry'}
                            </Button>
                        )}
                        <Button
                            size="small"
                            onClick={onClose}
                            sx={{
                                color: '#000',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                }
                            }}
                        >
                            Dismiss
                        </Button>
                    </Box>
                }
            />
        </Snackbar>
    );
};

export default RateLimitSnackbar;